import { useMemo } from 'react'
import { StyleSheet, Text, View, TouchableOpacity, Image } from 'react-native'
import { Entypo, FontAwesome } from '@expo/vector-icons'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { convertTo12HourTime } from '../../../../utils'
import { useLabourStore } from '../../../../stores/labour'
import { fontPalette } from '../../../../static/styles/global'

interface UpcomingShiftCardProps {
  shiftInfo: any[] | undefined
  navigation: any
}

const getPTODisplayTime = (ptohours: string | number) => {
  if (ptohours === undefined || ptohours === null) return ''
  const str = ptohours.toString()
  const [hours, minutes] = str.split('.')
  const formattedHours = hours.padStart(2, '0')
  const formattedMinutes = (minutes ? minutes.padEnd(2, '0') : '00').slice(0, 2)
  return `(Hrs. ${formattedHours}:${formattedMinutes})`
}

const UpcomingShiftCard = ({
  shiftInfo,
  navigation
}: UpcomingShiftCardProps): JSX.Element | null => {
  const { t } = useTranslation()
  const labourStore = useLabourStore()
  const ptoInfo = labourStore?.scheduleData?.ptoInfo

  // Get all shifts scheduled today
  const todayShifts = useMemo(() => {
    if (!shiftInfo || shiftInfo.length === 0) return []
    const todayStr = moment().format('YYYY-MM-DD')

    return shiftInfo
      .filter((item) => moment(item.date).format('YYYY-MM-DD') === todayStr)
      .sort((a, b) => {
        const aTime = a.time_In || '00:00:00'
        const bTime = b.time_In || '00:00:00'
        return aTime.localeCompare(bTime)
      })
  }, [shiftInfo])

  // Get any PTO available today
  const todayPto = useMemo(() => {
    if (!ptoInfo || ptoInfo.length === 0) return null
    const today = moment().startOf('day')
    return ptoInfo.find((item: any) => {
      const ptoStart = moment(item.startTimeOff).startOf('day')
      const ptoEnd = moment(item.endTimeOff).startOf('day')
      return today.isSameOrAfter(ptoStart) && today.isSameOrBefore(ptoEnd)
    })
  }, [ptoInfo])

  const showPtoOnly = todayShifts.length === 0 && !!todayPto

  if (todayShifts.length === 0 && !todayPto) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.iconCircle}>
            <FontAwesome name="calendar" size={16} color="white" />
          </View>
          <Text style={styles.headerTitle}>{t("Today's Schedule")}</Text>
        </View>
        <Text style={styles.noShiftText}>{t('No shifts scheduled today')}</Text>
        <TouchableOpacity
          style={[styles.linkButton, { marginTop: 10, alignSelf: 'flex-start' }]}
          onPress={() => navigation.navigate('LabourSavourSchedule')}
        >
          <Text style={styles.linkText}>{t('View full schedule')} </Text>
          <Entypo name="chevron-right" size={14} color="#A84499" />
        </TouchableOpacity>
      </View>
    )
  }

  const badgeText = t('Today')

  const showDot = !!todayPto
  const dotColor = todayPto ? (todayPto?.timeOffStatus === 1 ? 'green' : 'red') : ''

  return (
    <View style={styles.card}>
      <View style={styles.cardBody}>
        {/* Left Column: Shift/PTO Details */}
        <View style={styles.detailsCol}>
          <View style={styles.headerRow}>
            <View style={styles.iconCircle}>
              <FontAwesome name="calendar" size={16} color="white" />
            </View>
            <Text style={styles.headerTitle}>{t("Today's Shift")}</Text>
          </View>

          <View style={styles.badgeContainer}>
            <View style={[styles.badge, showDot && { flexDirection: 'row', alignItems: 'center' }]}>
              <Text style={styles.badgeText}>{badgeText}</Text>
              {showDot && (
                <View
                  style={[
                    styles.dot,
                    {
                      backgroundColor: dotColor
                    }
                  ]}
                />
              )}
            </View>
          </View>

          {showPtoOnly ? (
            <View style={styles.ptoOnlyContainer}>
              <Text
                style={[
                  styles.ptoStatusText,
                  { color: todayPto?.timeOffStatus === 1 ? 'green' : 'red', fontSize: 15 }
                ]}
              >
                {t(todayPto?.userTimeOffMesssage)}
              </Text>
              <Text style={[styles.ptoTimeText, { fontSize: 13, marginTop: 4 }]}>
                {moment(todayPto?.startTimeOff).format('YYYY-MM-DD') ===
                  moment(todayPto?.endTimeOff).format('YYYY-MM-DD') ? (
                  // Single day PTO
                  `${moment(todayPto?.startTimeOff).format('YYYY-MM-DD')} ${moment(todayPto?.startTimeOff).format('hh:mm A')} - ${moment(todayPto?.endTimeOff).format('hh:mm A')} ${getPTODisplayTime(todayPto?.ptohours)}`
                ) : (
                  // Multi-day PTO
                  `${moment(todayPto?.startTimeOff).format('MM/DD/YYYY')} ${moment(todayPto?.startTimeOff).format('hh:mm A')} - ${moment(todayPto?.endTimeOff).format('MM/DD/YYYY')} ${moment(todayPto?.endTimeOff).format('hh:mm A')} ${getPTODisplayTime(todayPto?.ptohours)}`
                )}
              </Text>
            </View>
          ) : (
            <>
              {todayShifts.map((shift, index) => {
                const inTime = convertTo12HourTime(shift.time_In)
                const outTime = convertTo12HourTime(shift.time_Out)
                const shiftTime = `${inTime} – ${outTime}`
                const isLast = index === todayShifts.length - 1
                return (
                  <View key={shift.shiftID || index}>
                    {index > 0 && <View style={styles.shiftDivider} />}
                    <Text style={styles.timeText}>{shiftTime}</Text>
                    <Text style={styles.deptText}>{shift.departmentName}</Text>
                    <Text style={[styles.storeText, (todayPto && isLast) && { marginBottom: 6 }]}>
                      {shift.storeName}
                    </Text>
                  </View>
                )
              })}

              {todayPto && (
                <View style={styles.ptoContainer}>
                  <Text
                    style={[
                      styles.ptoStatusText,
                      { color: todayPto?.timeOffStatus === 1 ? 'green' : 'red' }
                    ]}
                  >
                    {t(todayPto?.userTimeOffMesssage)}
                  </Text>
                  <Text style={styles.ptoTimeText}>
                    {moment(todayPto?.startTimeOff).format('YYYY-MM-DD') ===
                      moment(todayPto?.endTimeOff).format('YYYY-MM-DD') ? (
                      // Single day PTO
                      `${moment(todayPto?.startTimeOff).format('YYYY-MM-DD')} ${moment(todayPto?.startTimeOff).format('hh:mm A')} - ${moment(todayPto?.endTimeOff).format('hh:mm A')} ${getPTODisplayTime(todayPto?.ptohours)}`
                    ) : (
                      // Multi-day PTO
                      `${moment(todayPto?.startTimeOff).format('MM/DD/YYYY')} ${moment(todayPto?.startTimeOff).format('hh:mm A')} - ${moment(todayPto?.endTimeOff).format('MM/DD/YYYY')} ${moment(todayPto?.endTimeOff).format('hh:mm A')} ${getPTODisplayTime(todayPto?.ptohours)}`
                    )}
                  </Text>
                </View>
              )}
            </>
          )}

          <TouchableOpacity
            style={styles.linkButton}
            onPress={() => navigation.navigate('LabourSavourSchedule')}
          >
            <Text style={styles.linkText}>{t('View full schedule')} </Text>
            <Entypo name="chevron-right" size={14} color="#A84499" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  )
}

export default UpcomingShiftCard

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  cardBody: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  detailsCol: {
    flex: 1.3
  },
  illustrationCol: {
    flex: 0.7,
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12
  },
  iconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#A84499',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: fontPalette.primaryBold,
    color: '#333'
  },
  badgeContainer: {
    flexDirection: 'row',
    marginBottom: 8
  },
  badge: {
    backgroundColor: 'rgba(168, 68, 153, 0.1)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4
  },
  badgeText: {
    color: '#A84499',
    fontSize: 11,
    fontWeight: 'bold'
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginLeft: 4
  },
  ptoContainer: {
    marginTop: 4,
    marginBottom: 8
  },
  ptoOnlyContainer: {
    marginTop: 4,
    marginBottom: 12
  },
  ptoStatusText: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 2,
    fontFamily: fontPalette.primarySemiBold
  },
  ptoTimeText: {
    fontSize: 12,
    fontFamily: fontPalette.primaryBold,
    color: '#111'
  },
  timeText: {
    fontSize: 20,
    fontFamily: fontPalette.primaryBold,
    color: '#111',
    marginBottom: 6
  },
  deptText: {
    fontSize: 13,
    color: '#555',
    marginBottom: 2,
    fontFamily: fontPalette.primaryRegular
  },
  storeText: {
    fontSize: 12,
    color: '#777',
    textTransform: 'uppercase',
    marginBottom: 12
  },
  shiftDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 12
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4
  },
  linkText: {
    color: '#A84499',
    fontFamily: fontPalette.primaryBold,
    fontSize: 13
  },
  noShiftText: {
    fontSize: 14,
    color: '#777',
    textAlign: 'center',
    paddingVertical: 10,
    fontFamily: fontPalette.primaryRegular,
  },
  illustrationImage: {
    width: 175,
    height: 175
  }
})
